import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { knowledgeService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { KnowledgeBase, KnowledgeBaseFilters } from '../types/knowledge';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import KnowledgeBaseList from '../components/knowledge-base/KnowledgeBaseList';
import KnowledgeBaseForm from '../components/knowledge-base/KnowledgeBaseForm';
import KnowledgeBaseFiltersBar from '../components/knowledge-base/KnowledgeBaseFilters';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function KnowledgeBase() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingEntry, setEditingEntry] = useState<KnowledgeBase | null>(null);
  const [selectedAssistants, setSelectedAssistants] = useState<number[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [filters, setFilters] = useState<KnowledgeBaseFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: entriesResponse, isLoading } = useQuery({
    queryKey: ['knowledge-base', filters, currentPage],
    queryFn: () => knowledgeService.getEntries(filters, currentPage),
  });

  const { data: assistants = [] } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => knowledgeService.getAssistants(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<KnowledgeBase>) => knowledgeService.createEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
      messageHandler.success('Base de conhecimento criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: KnowledgeBase) => knowledgeService.updateEntry(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
      messageHandler.success('Base de conhecimento atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => knowledgeService.deleteEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
      messageHandler.success('Base de conhecimento excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (data: any) => {
    const formData = {
      ...data,
      tags,
      assistant_ids: selectedAssistants,
      metadata: {},
    };

    if (editingEntry) {
      updateMutation.mutate({ ...editingEntry, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (entry: KnowledgeBase) => {
    messageHandler.confirm(
      'Excluir Entrada',
      'Tem certeza que deseja excluir esta entrada da base de conhecimento?',
      () => deleteMutation.mutate(entry.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingEntry(null);
    setSelectedAssistants([]);
    setTags([]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (newFilters: KnowledgeBaseFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.category !== undefined || filters.search !== undefined;
  const totalPages = entriesResponse?.pagination?.total_pages || 1;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Base de Conhecimento"
          description="Gerencie sua base de conhecimento e vincule aos assistentes."
          actions={
            <Button
              onClick={() => {
                setEditingEntry(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Entrada
            </Button>
          }
        />

        <div className="mt-4 space-y-4">
          <KnowledgeBaseFiltersBar
            filters={filters}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
          />

          <KnowledgeBaseList
            entries={entriesResponse?.data || []}
            assistants={assistants}
            onEdit={entry => {
              setEditingEntry(entry);
              setSelectedAssistants(entry.assistant_ids || []);
              setTags(entry.tags || []);
              setModal({ type: 'edit' });
            }}
            onDelete={handleDelete}
            isLoading={isLoading}
            isFiltered={hasActiveFilters}
          />

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingEntry ? 'Editar Entrada' : 'Nova Entrada'}
          size="xl"
        >
          <KnowledgeBaseForm
            entry={editingEntry}
            assistants={assistants}
            selectedAssistants={selectedAssistants}
            onAssistantsChange={setSelectedAssistants}
            tags={tags}
            onTagsChange={setTags}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default KnowledgeBase;
